import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import AnimatedNumber from "../components/AnimatedNumber";
import CaseStudyHero from "../components/CaseStudyHero";
import { breadcrumbSchema } from "../lib/schemas";

const ApproachCard = ({ num, title, body, testId }) => (
  <div className="border border-dodgers/15 p-7 md:p-8 h-full" data-testid={testId}>
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-3">
      ● {num}
    </p>
    <h3 className="font-display text-[22px] md:text-[26px] leading-tight mb-3">{title}</h3>
    <p className="text-base md:text-[17px] leading-[1.7] text-mandrogni">{body}</p>
  </div>
);

const RelatedCard = ({ to, title, eyebrow }) => (
  <Link
    to={to}
    className="block border border-dodgers/15 p-6 md:p-7 hover:border-lime transition-colors group"
    data-testid={`related-${to.replace(/[^a-z0-9]+/g, "-")}`}
  >
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-2">
      {eyebrow}
    </p>
    <p className="font-display text-[20px] md:text-[22px] leading-tight group-hover:text-lime transition-colors">
      {title}
    </p>
    <span className="inline-block mt-4 font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
      Vedi il case →
    </span>
  </Link>
);

const WorkSportlinx360 = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Sportlinx360", url: "/works/sportlinx360" },
  ]);

  return (
    <>
      <Seo
        title="Sportlinx360: 2.500+ lead in 3 anni per college USA | OnSports Case"
        description="Lead generation Sportlinx360: 2.500+ lead in 3 anni per borse studio sportive nei college USA. Meta + Google Ads + content con Tutto Volley e Indre Sorokaite."
        path="/works/sportlinx360"
        image="/images/works/sportlinx360/thumbnail.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/sportlinx360/hero.gif"
        imageAlt="Sportlinx360 — Lead generation per studenti-atleti USA"
        eyebrow="● Case study · Business Impact · Cultural Impact"
        titleLines={["Sportlinx360.", "3 anni, 2.500+ lead,", "una nuova GENERAZIONE."]}
        highlightWord="GENERAZIONE."
        subtitle="Lead generation Meta + Google Ads e content con creator volley per intercettare giovani atleti italiani interessati alle borse di studio nei college USA."
        meta="2023 — In corso  ·  Cliente — Sportlinx360  ·  Canali — Meta Ads + Google Ads + Email Marketing  ·  Creator — Tutto Volley + Indre Sorokaite"
        overlayOpacity={0.5}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-overview">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Overview</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Una mission specifica. Un target preciso. Una macchina di acquisizione che funziona
              da 3 anni.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Sportlinx360 è un'azienda romana con oltre 15 anni di attività dedicata a un
                percorso poco conosciuto in Italia: aiutare giovani studenti-atleti italiani a
                ottenere borse di studio sportive nei college statunitensi. Il loro target è
                specifico — pallavoliste e pallavolisti agonisti, e i loro genitori — e prima del
                nostro lavoro l'acquisizione di nuovi candidati avveniva soprattutto via passaparola
                e canali tradizionali.
              </p>
              <p>
                Da 3 anni OnSports gestisce per loro tutta la macchina di lead generation digitale:
                campagne Meta e Google Ads su pubblici in target, contenuti prodotti con i due
                punti di riferimento italiani del volley digitale (Tutto Volley e Indre Sorokaite),
                email marketing sulla user base acquisita. Risultato: oltre 2.500 lead qualificati
                in 3 anni — atleti agonisti reali e genitori che valutano concretamente il percorso
                US per i loro figli.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-sfida">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-lime">
              ● La sfida
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Come si comunica un percorso complesso a un pubblico che non sa nemmeno che esiste?
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-galactico/85">
              <p>
                La maggior parte dei giovani pallavolisti italiani — anche quelli ad alto livello
                agonistico — non considera il college americano come opzione concreta. È un percorso
                poco raccontato, percepito come irraggiungibile, costoso o riservato a pochi. Il
                primo problema strategico di Sportlinx360 non era quindi "venderne" il servizio.
                Era educare un pubblico che ignorava l'esistenza stessa di un'opportunità.
              </p>
              <p>
                Su questo si è costruita la strategia digitale: le campagne adv non vendono un
                prodotto, raccontano un percorso. I contenuti dei creator non sono testimonianze
                "il servizio funziona", sono storie di student-athlete reali che hanno vissuto il
                percorso. La performance pubblicitaria diventa funzione della qualità narrativa,
                non solo del targeting.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 4 — L'APPROCCIO */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-approccio">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>● L'approccio</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6">Quattro asset paralleli, una sola direzione.</h2>
            </RevealOnScroll>
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproachCard
                num="01"
                title="Meta Ads"
                body="Campagne Meta su pubblici in target: pallavolisti/e agonisti, genitori di figli sportivi, comunità tematiche del volley italiano. Iterazione settimanale di creatività, audience e copy. Focus su CPL (cost per lead) e qualità del lead, non sul volume puro."
                testId="approach-01"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <ApproachCard
                num="02"
                title="Google Ads"
                body='Search ads su query informative ("borse di studio sport USA", "college americano pallavolo", "studente atleta USA") + campagne Performance Max per intercettare anche pubblici di intento alto in browser e YouTube.'
                testId="approach-02"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <ApproachCard
                num="03"
                title="Content con creator"
                body="Contenuti video prodotti con i due principali referenti del volley digitale italiano: Tutto Volley (community di riferimento per il settore) e Indre Sorokaite (ex-pallavolista azzurra, voce autorevole sul tema career path nello sport). Contenuti distribuiti sia organicamente sui loro canali sia dentro le campagne adv."
                testId="approach-03"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <ApproachCard
                num="04"
                title="Email marketing"
                body="Lavoro sulla user base acquisita via lead gen: nurture sequences, contenuti educational sul percorso student-athlete, eventi e webinar, conversione da lead a candidato attivo."
                testId="approach-04"
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — VIDEO MANIFESTO */}
      <section className="bg-dodgers text-galactico py-16 md:py-24" data-testid="section-video">
        <div className="container-os">
          <div className="max-w-[1100px] mx-auto">
            <WistiaPlayer mediaId="76l0qifkya" aspect={1.8972} />
          </div>
          <p className="mt-6 text-center text-[14px] italic text-galactico/60 max-w-[60ch] mx-auto">
            Il video manifesto della campagna Sportlinx360.
          </p>
        </div>
      </section>

      {/* SEZ. 6 — I CREATOR */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-creator">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● I creator</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6">Le voci giuste, non quelle più grandi.</h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 text-[17px] leading-[1.7] text-mandrogni">
              La scelta dei creator non ha seguito la logica del follower count massimo. Ha seguito
              la logica della rilevanza specifica nel target. Tutto Volley è la community digitale
              di riferimento per il movimento volley italiano — chi gioca a volley a livello
              agonistico la legge, ne parla, ne condivide i contenuti. Indre Sorokaite è una
              ex-pallavolista azzurra, una voce che il target riconosce immediatamente come "una di
              noi" — non un brand ambassador esterno, una persona che ha vissuto da dentro un
              percorso sportivo di alto livello. Insieme costituiscono la combinazione più
              credibile possibile per un pubblico volley italiano che valuta un cambiamento di
              vita.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 7 — I REEL */}
      <section className="bg-dodgers text-galactico py-12 md:py-16" data-testid="section-reels">
        <div className="container-os">
          <div className="max-w-[800px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="max-w-[380px] mx-auto w-full">
              <WistiaPlayer mediaId="bwukyf6bmb" aspect={9 / 16} />
            </div>
            <div className="max-w-[380px] mx-auto w-full">
              <WistiaPlayer mediaId="n1bpx8ie4k" aspect={9 / 16} />
            </div>
          </div>
          <p className="mt-6 text-center text-[14px] italic text-galactico/60 max-w-[60ch] mx-auto">
            Due dei reel della campagna, prodotti coi creator partner.
          </p>
        </div>
      </section>

      {/* SEZ. 8 — L'IMPATTO */}
      <section className="bg-galactico text-dodgers py-24 md:py-32" data-testid="section-impatto">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto text-center">
            <RevealOnScroll>
              <SectionEyebrow>● L'impatto</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6 max-w-[22ch] mx-auto">
                Numeri che convertono in <span className="text-lime">VITE</span>.
              </h2>
            </RevealOnScroll>
          </div>

          {/* KPI animati */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-10 max-w-[1100px] mx-auto">
            <div className="text-center min-w-0" data-testid="kpi-lead">
              <p
                className="font-display text-lime leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(72px, 10vw, 140px)" }}
              >
                <AnimatedNumber end={2500} suffix="+" />
              </p>
              <p className="mt-6 font-condensed uppercase tracking-widest text-[14px] text-dodgers/85">
                Lead qualificati in 3 anni
              </p>
            </div>
            <div className="text-center min-w-0" data-testid="kpi-anni">
              <p
                className="font-display text-lime leading-none whitespace-nowrap"
                style={{ fontSize: "clamp(72px, 10vw, 140px)" }}
              >
                <AnimatedNumber end={3} />
              </p>
              <p className="mt-6 font-condensed uppercase tracking-widest text-[14px] text-dodgers/85">
                Anni di collaborazione continuativa
              </p>
            </div>
          </div>

          <RevealOnScroll delay={0.15}>
            <div className="mt-16 md:mt-20 max-w-[760px] mx-auto text-center">
              <p className="text-[17px] leading-[1.7] text-mandrogni">
                2.500+ lead in 3 anni non è un numero di vanity. È il volume di famiglie italiane
                che hanno scoperto, valutato e in molti casi intrapreso un percorso di
                studente-atleta nei college USA grazie a una macchina di lead gen costruita su
                targeting preciso, content credibile e iterazione costante. Per Sportlinx360 ogni
                lead in target è potenzialmente un futuro college athlete italiano negli Stati
                Uniti.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-credits">
        <div className="container-os max-w-[720px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2
              className="font-display leading-tight mt-6"
              style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}
            >
              Chi ha fatto cosa.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <dl className="mt-10 divide-y divide-dodgers/15">
              {[
                ["Strategia & Performance Marketing", "OnSports"],
                ["Gestione Meta Ads + Google Ads", "OnSports"],
                ["Email Marketing", "OnSports"],
                ["Content production", "OnSports + Tutto Volley + Indre Sorokaite"],
                ["Cliente", "Sportlinx360"],
                ["Periodo", "2023 — In corso"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="py-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-6"
                >
                  <dt className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni">
                    {label}
                  </dt>
                  <dd className="text-[16px]">{value}</dd>
                </div>
              ))}
            </dl>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA + NAVIGATION */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-32 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-[760px]">
          <RevealOnScroll>
            <h2
              className="font-display leading-tight"
              style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}
            >
              Hai un funnel di acquisizione da costruire?{" "}
              <span className="text-lime">PARLIAMONE</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.08}>
            <p className="mt-6 text-[16px] md:text-[17px] leading-[1.7] text-galactico/85 max-w-[600px]">
              Una call iniziale di 30 minuti per capire se le tue conversioni possono diventare più
              prevedibili.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.16}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-perf-marketing">
                <Button variant="secondary-light">Vedi altri case Performance Marketing</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="section-related">
        <div className="container-os">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni mb-8">
              ● Altri case correlati
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <RelatedCard
              to="/works/empower"
              eyebrow="Business Impact · Brand & Performance"
              title="Empower"
            />
            <RelatedCard
              to="/works/lvf-tv"
              eyebrow="Business Impact · Subscriber Acquisition"
              title="LVF TV"
            />
            <RelatedCard
              to="/works/protek"
              eyebrow="Business Impact · DTC Sportswear"
              title="Protek"
            />
          </div>
          <div className="mt-10">
            <Link
              to="/works"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-[13px] text-dodgers hover:text-lime transition-colors"
              data-testid="back-to-works"
            >
              <ArrowLeft size={16} /> Tutti i casi studio
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkSportlinx360;
